// external
import React, { FC, useEffect, useMemo, useState } from 'react';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// internal
import useService from '../../hooks/service.hook';
import EquipmentService from '../../services/equipments.service';
import { getSportMappings } from '../../helpers/app-context.helpers';

interface AddEquipmentModalProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  /** When creating equipment on behalf of a managed athlete, their user id. */
  athleteUserId?: number;
  /** Display name of the athlete when creating on their behalf. */
  athleteName?: string;
}

// Reusable section header with a colored icon box
const SectionHeader: FC<{
  label: string;
  iconBg: string;
  iconColor: string;
  icon: React.ReactNode;
}> = ({ label, iconBg, iconColor, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: 1,
        bgcolor: iconBg,
        color: iconColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
  </Box>
);

const AddEquipmentModal: FC<AddEquipmentModalProps> = (props) => {
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [loading, setLoading] = useState(false);
  const [equipmentTypes, setEquipmentTypes] = useState<any[]>([]);
  const [selectedType, setSelectedType] = useState<any | null>(null);
  const [selectedSubtype, setSelectedSubtype] = useState<any | null>(null);
  const [subtypes, setSubtypes] = useState<any[]>([]);
  const [standardFields, setStandardFields] = useState<any[]>([]);
  const [standardFieldValues, setStandardFieldValues] = useState<{ [key: string]: string }>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [errors, setErrors] = useState({ name: false, brand: false, year: false });
  const [defaultSport, setDefaultSport] = useState('');
  const [defaultSportType, setDefaultSportType] = useState('');

  const equipmentService = useService(EquipmentService);
  const sportMappings = useMemo(() => getSportMappings(), []);

  const sportOptions: string[] = useMemo(() => sportMappings.map((s: any) => s.name), [sportMappings]);

  const sportTypeOptions: string[] = useMemo(() => {
    if (!defaultSport) return [];
    const sport = sportMappings.find((s: any) => s.name === defaultSport);
    return (sport?.sport_types ?? []).map((st: any) => st.name);
  }, [defaultSport, sportMappings]);

  // Reset all form state when the modal opens/closes
  useEffect(() => {
    if (!props.open) return;
    setName('');
    setBrand('');
    setYear(String(new Date().getFullYear()));
    setSelectedType(null);
    setSelectedSubtype(null);
    setStandardFieldValues({});
    setAlert(null);
    setErrors({ name: false, brand: false, year: false });
    setDefaultSport('');
    setDefaultSportType('');
  }, [props.open]);

  // Fetch equipment types when the modal opens
  useEffect(() => {
    if (!props.open) return;
    setLoading(true);
    equipmentService
      .getEquipmentTypes()
      .then((res) => {
        setEquipmentTypes(res.data.data || []);
      })
      .catch((error) => {
        console.error('Failed to fetch equipment types:', error);
      })
      .finally(() => setLoading(false));
  }, [props.open, equipmentService]);

  // Derive subtypes and standard fields from the selected type
  useEffect(() => {
    if (selectedType) {
      setSubtypes(selectedType.subtypes || []);
      setStandardFields(selectedType.standard_fields || []);
      setSelectedSubtype(null);
    } else {
      setSubtypes([]);
      setStandardFields([]);
      setSelectedSubtype(null);
    }
  }, [selectedType]);

  // Merge subtype standard fields on top of type standard fields
  useEffect(() => {
    if (selectedSubtype) {
      setStandardFields([...(selectedType?.standard_fields || []), ...(selectedSubtype.standard_fields || [])]);
    } else if (selectedType) {
      setStandardFields(selectedType.standard_fields || []);
    } else {
      setStandardFields([]);
    }
  }, [selectedSubtype, selectedType]);

  // Clear standard field values when type/subtype changes (new equipment only)
  useEffect(() => {
    setStandardFieldValues({});
  }, [selectedType, selectedSubtype]);

  const humanizeLabel = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  const validateFields = (): boolean => {
    let valid = true;
    let alertMsg = '';
    const newErrors = {
      name: !name.trim(),
      brand: !brand.trim(),
      year: !year?.trim() || !/^\d{4}$/.test(year?.trim()),
    };
    if (newErrors.name) {
      alertMsg = 'Equipment name is required.';
      valid = false;
    } else if (newErrors.brand) {
      alertMsg = 'Brand is required.';
      valid = false;
    } else if (newErrors.year) {
      alertMsg = 'Year is required and must be a 4-digit year (e.g., 2001).';
      valid = false;
    } else if (!selectedType) {
      alertMsg = 'Equipment type is required.';
      valid = false;
    }
    setErrors(newErrors);
    setAlert(valid ? null : alertMsg);
    return valid;
  };

  const handleSubmit = () => {
    if (!validateFields()) return;
    const payload: any = {
      name: name.trim(),
      brand: brand.trim(),
      year: parseInt(year?.trim(), 10),
      equipment_type_id: selectedType?.id,
      standard_field_values: { ...standardFieldValues },
    };
    if (selectedSubtype) payload.equipment_subtype_id = selectedSubtype.id;
    if (props.athleteUserId != null) payload.athlete_user_id = props.athleteUserId;
    if (defaultSport && defaultSportType) {
      const sport = sportMappings.find((s: any) => s.name === defaultSport);
      const sportType = sport?.sport_types?.find((st: any) => st.name === defaultSportType);
      if (sport && sportType) {
        payload.default_sport_id = sport.id;
        payload.default_sport_type_id = sportType.id;
      }
    }
    equipmentService
      .createEquipmentV2(payload)
      .then(() => props.onClose(true))
      .catch(() => {
        setAlert('Failed to create equipment. Please try again.');
      });
  };

  return (
    <Modal
      open={props.open}
      onClose={() => props.onClose()}
      aria-labelledby="add-equipment-modal-title"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}
    >
      <Paper
        sx={{
          width: 'min(600px, 95vw)',
          maxHeight: '90vh',
          padding: '24px',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: '#e8f0fe',
                color: '#3c5dd4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" id="add-equipment-modal-title" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Add New Equipment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {props.athleteName
                  ? `Creating equipment for ${props.athleteName}`
                  : 'Fill in the details to register your gear'}
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Close" disableInteractive>
            <IconButton onClick={() => props.onClose()} size="small" sx={{ mt: 0.5 }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Scrollable form body */}
        <Box sx={{ overflowY: 'auto', flex: 1, pr: 0.5 }}>
          {alert && (
            <Alert severity="error" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: 13 } }}>
              {alert}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* General Info */}
              <Box sx={{ mb: 2.5 }}>
                <SectionHeader
                  label="General Info"
                  iconBg="#e8f0fe"
                  iconColor="#3c5dd4"
                  icon={
                    <svg
                      width="15"
                      height="15"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="2" width="10" height="10" rx="1.5" />
                      <path d="M4 6.5h6M4 5h4" />
                    </svg>
                  }
                />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Equipment Name *"
                      variant="outlined"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      error={errors.name}
                      helperText={errors.name ? 'Equipment name is required' : ''}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Brand *"
                      variant="outlined"
                      value={brand}
                      onChange={(e) => setBrand(e.target.value)}
                      error={errors.brand}
                      helperText={errors.brand ? 'Brand is required' : ''}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Year *"
                      variant="outlined"
                      type="number"
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      error={errors.year}
                      helperText={errors.year ? 'Year is required' : ''}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Equipment Category */}
              <Box sx={{ mb: 2.5 }}>
                <SectionHeader
                  label="Equipment Category"
                  iconBg="#ede7f6"
                  iconColor="#7c3aed"
                  icon={
                    <svg
                      width="15"
                      height="15"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M2 2h11l1 5H1l1-5zM1 7h13v5a1 1 0 01-1 1H2a1 1 0 01-1-1V7z" />
                    </svg>
                  }
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={subtypes.length > 0 ? 6 : 12}>
                    <Select
                      fullWidth
                      size="small"
                      value={selectedType?.id || ''}
                      onChange={(e) => {
                        const type = equipmentTypes.find((t) => t.id === e.target.value);
                        setSelectedType(type || null);
                      }}
                      displayEmpty
                      MenuProps={{ PaperProps: { sx: { zIndex: 1400 } }, container: () => document.body }}
                      renderValue={(value) => {
                        if (!value) return <em>Select Equipment Type</em>;
                        const type = equipmentTypes.find((t) => t.id === value);
                        return type?.name || '';
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select Equipment Type</em>
                      </MenuItem>
                      {equipmentTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  {subtypes.length > 0 && (
                    <Grid item xs={12} sm={6}>
                      <Select
                        fullWidth
                        size="small"
                        value={selectedSubtype?.id || ''}
                        onChange={(e) => {
                          const subtype = subtypes.find((s) => s.id === e.target.value);
                          setSelectedSubtype(subtype || null);
                        }}
                        displayEmpty
                        disabled={!selectedType}
                        MenuProps={{ PaperProps: { sx: { zIndex: 1400 } }, container: () => document.body }}
                        renderValue={(value) => {
                          if (!value) return <em>Select Subtype</em>;
                          const subtype = subtypes.find((s) => s.id === value);
                          return subtype?.name || '';
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select Subtype</em>
                        </MenuItem>
                        {subtypes.map((subtype) => (
                          <MenuItem key={subtype.id} value={subtype.id}>
                            {subtype.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Equipment Details */}
              {standardFields.length > 0 && (
                <Box sx={{ mb: 2.5 }}>
                  <SectionHeader
                    label="Equipment Details"
                    iconBg="#e6f7ef"
                    iconColor="#0d9f6e"
                    icon={
                      <svg
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 13V3l5.5 5L12 3v10" />
                      </svg>
                    }
                  />
                  <Grid container spacing={2}>
                    {standardFields.map((field: any) => {
                      const label = field.required ? `${humanizeLabel(field.name)} *` : humanizeLabel(field.name);
                      const options: string[] =
                        field.type === 'dropdown' && field.options?.options ? field.options.options : [];
                      return (
                        <Grid item xs={12} sm={6} key={field.name}>
                          {field.type === 'dropdown' && options.length > 0 ? (
                            <FormControl fullWidth variant="outlined" size="small" required={field.required}>
                              <InputLabel id={`${field.name}-label`}>{label}</InputLabel>
                              <Select
                                labelId={`${field.name}-label`}
                                label={label}
                                size="small"
                                value={standardFieldValues[field.name] || ''}
                                onChange={(e) =>
                                  setStandardFieldValues((prev) => ({ ...prev, [field.name]: e.target.value }))
                                }
                              >
                                {!field.required && (
                                  <MenuItem value="">
                                    <em>None</em>
                                  </MenuItem>
                                )}
                                {options.map((opt: string) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              label={label}
                              variant="outlined"
                              value={standardFieldValues[field.name] || ''}
                              onChange={(e) =>
                                setStandardFieldValues((prev) => ({ ...prev, [field.name]: e.target.value }))
                              }
                              required={field.required}
                              InputProps={
                                field.measurement_unit
                                  ? {
                                      endAdornment: (
                                        <InputAdornment position="end">{field.measurement_unit}</InputAdornment>
                                      ),
                                    }
                                  : undefined
                              }
                            />
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {/* Set as Default */}
              <Box sx={{ mb: 2.5 }}>
                <SectionHeader
                  label="Set as Default"
                  iconBg="#e8f0fe"
                  iconColor="#3c5dd4"
                  icon={
                    <svg
                      width="15"
                      height="15"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M7.5 1l1.5 3.2 3.5.5-2.5 2.4.6 3.4-3.1-1.6-3.1 1.6.6-3.4L2.5 4.7l3.5-.5z" />
                    </svg>
                  }
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Select
                      fullWidth
                      size="small"
                      value={defaultSport}
                      onChange={(e) => {
                        setDefaultSport(e.target.value);
                        setDefaultSportType('');
                      }}
                      displayEmpty
                      MenuProps={{ PaperProps: { sx: { zIndex: 1400 } }, container: () => document.body }}
                      renderValue={(value) => (!value ? <em>Select Sport</em> : value)}
                    >
                      <MenuItem value="" disabled>
                        <em>Select Sport</em>
                      </MenuItem>
                      {sportOptions.map((sport) => (
                        <MenuItem key={sport} value={sport}>
                          {sport}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Select
                      fullWidth
                      size="small"
                      value={defaultSportType}
                      onChange={(e) => setDefaultSportType(e.target.value)}
                      displayEmpty
                      disabled={!defaultSport || sportTypeOptions.length === 0}
                      MenuProps={{ PaperProps: { sx: { zIndex: 1400 } }, container: () => document.body }}
                      renderValue={(value) => (!value ? <em>Select Discipline</em> : value)}
                    >
                      <MenuItem value="" disabled>
                        <em>Select Discipline</em>
                      </MenuItem>
                      {sportTypeOptions.map((st) => (
                        <MenuItem key={st} value={st}>
                          {st}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              </Box>

              {/* Submit */}
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={<AddCircleOutlineIcon />}
                sx={{ borderRadius: 6, py: 1.25, fontWeight: 600, fontSize: 15 }}
              >
                Add Equipment
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

export default AddEquipmentModal;
